import { useBlocker } from "@tanstack/react-router"
import type { ReactNode } from "react"

import Commandbar from "~/components/planning/Commandbar"
import { Button } from "~/components/reusable/Button"
import { ConfirmationDialog } from "~/components/reusable/ConfirmationDialog"

type UnsavedChangesBarProps = {
  isDirty: boolean
  onSave: () => void
  onDiscard: () => void
  isSaving?: boolean
  /** Shown as "N unsaved changes"; omit when the page can't count its edits. */
  changeCount?: number
  saveDisabled?: boolean
  saveAllowedTitle?: string
  /** Set false when an ancestor (e.g. a provider spanning several tabs) already blocks navigation. */
  blockNavigation?: boolean
  /** Extra actions rendered before Discard. */
  children?: ReactNode
  /** A selection bar is showing below, so this one sits just above it (stacked). */
  raised?: boolean
  inline?: boolean
}

/** Bottom Commandbar with Discard / Save while dirty; leaving the page asks to confirm. */
export default function UnsavedChangesBar({
  isDirty,
  onSave,
  onDiscard,
  isSaving = false,
  changeCount,
  saveDisabled = false,
  saveAllowedTitle,
  blockNavigation = true,
  children,
  raised = false,
  inline = false,
}: UnsavedChangesBarProps) {
  const hasCount = changeCount !== undefined

  return (
    <>
      <Commandbar
        count={isDirty ? Math.max(changeCount ?? 1, 1) : 0}
        selectedText={changeCount === 1 ? "unsaved change" : "unsaved changes"}
        hideHeader={!hasCount}
        ariaLabel="Unsaved changes"
        raised={raised}
        inline={inline}
      >
        {!hasCount && <span className="label-base pr-1">Unsaved changes</span>}
        {children}
        <Button size="xs" variant="neutral" onClick={onDiscard} disabled={isSaving}>
          Discard
        </Button>
        <Button
          size="xs"
          requires="edit"
          allowedTitle={saveAllowedTitle}
          onClick={onSave}
          disabled={saveDisabled}
          loading={isSaving}
        >
          Save
        </Button>
      </Commandbar>
      <UnsavedChangesGuard when={blockNavigation && isDirty} />
    </>
  )
}

/**
 * Asks before leaving the page while `when` holds. For pages whose save controls live elsewhere.
 * Navigation within `scopePath` (tabs sharing the unsaved state) isn't blocked.
 */
export function UnsavedChangesGuard({ when, scopePath }: { when: boolean; scopePath?: string }) {
  const blocker = useBlocker({
    shouldBlockFn: ({ next }) => when && !(scopePath && isWithinPath(next.pathname, scopePath)),
    enableBeforeUnload: when,
    withResolver: true,
  })
  return (
    <ConfirmationDialog
      open={blocker.status === "blocked"}
      onOpenChange={(open) => !open && blocker.reset?.()}
      title="Discard unsaved changes?"
      description="You have changes on this page that haven't been saved."
      confirmText="Discard changes"
      variant="danger"
      onConfirm={() => blocker.proceed?.()}
      onCancel={() => blocker.reset?.()}
    />
  )
}

function isWithinPath(pathname: string, scopePath: string) {
  return pathname === scopePath || pathname.startsWith(`${scopePath}/`)
}
